import {
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Title,
  TextInput,
} from '@patternfly/react-core'
import parsePhoneNumberLib, { AsYouType } from 'libphonenumber-js'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { UpdatePatientStatus } from '@app/components/patient/patient-card/patient-status'
import {
  getPhoneTypeLabel,
  emailAddressFromPatient,
  phoneNumberFromPatient,
  phoneNumberTypePatient,
} from '@app/domain/patient/patient.helpers'
import type {
  IPatient,
  IPatientEmailAddress,
  IPatientPhoneNumber,
} from '@app/domain/patient/patient.interface'
import { PhoneNumberTypeId } from '@app/domain/patient/patient.interface'
import { hasContent } from '@app/lib/string'
import { emailValidator, ValidationStatusId } from '@app/lib/validators'
import type * as Patients from '@app/state/patients'
import * as messages from '@app/state/patients/messages'
import { SmallButton } from '@app/utils/styles/small-button'

interface Props {
  onCancel: () => void
  onUpdate?: (
    id: string,
    patient: Patients.IApiUpdateContactInformation,
  ) => void
  patient: IPatient
  updateStatus?: Patients.PatientActionStatusId
}

export const EditableContactCard = ({
  patient,
  onCancel,
  onUpdate,
  updateStatus,
}: Props) => {
  const [emailAddress, setEmailAddress] = useState<IPatientEmailAddress>('')
  const [phoneNumber, setPhoneNumber] = useState<IPatientPhoneNumber>('')
  const [phoneNumberType, setPhoneNumberType] = useState<PhoneNumberTypeId>(
    PhoneNumberTypeId.null,
  )
  const [emailValidatorText, setEmailValidatorText] = useState<string>('')
  const [phoneValidatorText, setPhoneValidatorText] = useState<string>('')
  const prevPhoneRef = React.useRef<string>('')

  useEffect(() => {
    setEmailAddress(emailAddressFromPatient(patient.contactInformation) ?? '')
    const initialPhoneNumber =
      phoneNumberFromPatient(patient.contactInformation) ?? ''
    setPhoneNumber(initialPhoneNumber)
    setPhoneNumberType(
      phoneNumberTypePatient(patient.contactInformation) ??
        PhoneNumberTypeId.null,
    )
  }, [patient, setPhoneNumber])

  const handleSetPhoneType = useCallback((newPhoneType: string) => {
    setPhoneNumberType(newPhoneType as PhoneNumberTypeId)
  }, [])

  const handlePhoneChange = React.useCallback(
    (value: string, e?: React.FormEvent<HTMLInputElement>) => {
      const inputElement = e?.currentTarget
      const selStart = inputElement?.selectionStart ?? value.length
      const selEnd = inputElement?.selectionEnd ?? value.length
      const caretAtEnd = selStart === value.length && selEnd === value.length

      const wasDeletion = value.length < (prevPhoneRef.current?.length ?? 0)

      let next = value

      if (!wasDeletion && caretAtEnd) {
        next = new AsYouType('CA').input(value)
      }

      setPhoneNumber(next)
      prevPhoneRef.current = next
    },
    [],
  )

  const handlePhoneBlur = React.useCallback(() => {
    setPhoneNumber((prev) => new AsYouType('CA').input(prev))
    prevPhoneRef.current = new AsYouType('CA').input(prevPhoneRef.current || '')
  }, [])

  const validateEmail = useCallback(
    (email: IPatientEmailAddress) => {
      if (email !== '') {
        setEmailValidatorText('Please enter a valid email.')
        return emailValidator(email)
          ? ValidationStatusId.Success
          : ValidationStatusId.Error
      }
      if (phoneNumber === '') {
        setEmailValidatorText(
          'A valid email is required if no phone number is provided.',
        )
        return hasContent(email) && emailValidator(email)
          ? ValidationStatusId.Success
          : ValidationStatusId.Error
      } else {
        return ValidationStatusId.Default
      }
    },
    [phoneNumber],
  )

  const validatePhone = useCallback(
    (phone: IPatientPhoneNumber) => {
      const trimmed = phone.trim()
      const noPhone = trimmed === ''

      if (noPhone) {
        setPhoneNumberType(PhoneNumberTypeId.null)
        if (emailAddress.trim() === '') {
          setPhoneValidatorText(messages.phoneValidatorNoEmail)
          return ValidationStatusId.Error
        }
        return ValidationStatusId.Default
      }

      const parsed = parsePhoneNumberLib(trimmed, 'CA')
      if (!parsed?.isValid()) {
        setPhoneValidatorText('Invalid phone number.')
        return ValidationStatusId.Error
      }

      return ValidationStatusId.Success
    },
    [emailAddress],
  )

  const validatePhoneType = useCallback(
    (newPhoneType: PhoneNumberTypeId): ValidationStatusId => {
      if (newPhoneType === PhoneNumberTypeId.null) {
        if (phoneNumber.trim() !== '') {
          return ValidationStatusId.Error
        }
      } else {
        return ValidationStatusId.Default
      }
      return ValidationStatusId.Default
    },
    [phoneNumber],
  )

  const {
    emailValidation,
    phoneValidation,
    phoneTypeValidation,
  } = useMemo(() => {
    return {
      emailValidation: validateEmail(emailAddress),
      phoneValidation: validatePhone(phoneNumber),
      phoneTypeValidation: validatePhoneType(phoneNumberType),
    }
  }, [
    emailAddress,
    phoneNumber,
    phoneNumberType,
    validateEmail,
    validatePhone,
    validatePhoneType,
  ])

  const isFormValid = useMemo(() => {
    if (
      validateEmail(emailAddress) === ValidationStatusId.Error ||
      validatePhone(phoneNumber) === ValidationStatusId.Error ||
      validatePhoneType(phoneNumberType) === ValidationStatusId.Error
    ) {
      return false
    } else {
      return true
    }
  }, [
    emailAddress,
    phoneNumber,
    phoneNumberType,
    validateEmail,
    validatePhone,
    validatePhoneType,
  ])

  const handleUpdateContact = useCallback(() => {
    if (isFormValid) {
      const parsed = parsePhoneNumberLib(phoneNumber, 'CA')
      const normalizedPhone = parsed?.isValid()
        ? parsed.format('E.164')
        : phoneNumber

      const updatedContact = {
        ...patient,
        emailAddress,
        phoneNumber: normalizedPhone,
        phoneNumberType,
      }

      onUpdate(patient.id, updatedContact)
    }
  }, [
    emailAddress,
    phoneNumber,
    phoneNumberType,
    isFormValid,
    onUpdate,
    patient,
  ])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      handleUpdateContact()
    },
    [handleUpdateContact],
  )

  return (
    <Flex direction={{ default: 'column' }}>
      <FlexItem>
        <Flex direction={{ default: 'row' }}>
          <FlexItem>
            <Title headingLevel="h2" size="lg">
              Contact Information
            </Title>
          </FlexItem>
          <FlexItem>
            <SmallButton variant="secondary" onClick={onCancel}>
              Cancel
            </SmallButton>
          </FlexItem>
          <FlexItem>
            <SmallButton
              variant="primary"
              isDisabled={!isFormValid}
              onClick={handleUpdateContact}
            >
              Save
            </SmallButton>
          </FlexItem>
          <FlexItem>
            <UpdatePatientStatus updateStatus={updateStatus} />
          </FlexItem>
        </Flex>
      </FlexItem>
      <Form isHorizontal onSubmit={handleSubmit}>
        <FormGroup
          label="Email Address"
          fieldId="email-address"
          helperTextInvalid={emailValidatorText}
          validated={emailValidation}
        >
          <TextInput
            value={emailAddress}
            type="text"
            id="email-address"
            name="emailAddress"
            onChange={setEmailAddress}
          />
        </FormGroup>
        <FormGroup
          label="Phone Number"
          fieldId="phone-number"
          helperTextInvalid={phoneValidatorText}
          validated={phoneValidation}
        >
          <TextInput
            value={phoneNumber}
            type="tel"
            id="phone-number"
            name="phoneNumber"
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
            placeholder="+1 (234) 567-8900"
          />
        </FormGroup>
        <FormGroup
          label="Phone Type"
          fieldId="phone-type"
          helperTextInvalid="Phone Type is required"
          validated={phoneTypeValidation}
        >
          <FormSelect
            value={phoneNumberType}
            validated={phoneTypeValidation}
            onChange={handleSetPhoneType}
          >
            {Object.entries(PhoneNumberTypeId).map(([key, val]) => (
              <FormSelectOption
                key={key}
                value={PhoneNumberTypeId[key] as PhoneNumberTypeId}
                label={getPhoneTypeLabel(val)}
              />
            ))}
          </FormSelect>
        </FormGroup>
      </Form>
    </Flex>
  )
}
